// src/components/FilePreview.js - With simplified PDF viewer and enhanced image-PDF handling
// UPDATED: Added HTML preview support with zooming capability and fixed double scrollbar issue
import React, { useState, useEffect, useRef } from 'react';
import './FilePreview.css';
import { extractTextFromPDF, generatePDFThumbnail, createImageBasedPdfThumbnail, generatePdfErrorThumbnail } from '../fileUtils/pdfUtils';
import { parseCSV, parseExcel, formatCSVAsTable, formatExcelAsHtml } from '../fileUtils/spreadsheetUtils';
import Spinner from './Spinner'; // Import the new Spinner component

/**
 * FilePreview - Enhanced component for displaying file previews
 * Contains all preview functionality in a single file with internal component organization
 * UPDATED:
 * 1. PDF viewer now has no tabs or buttons
 * 2. Enhanced handling for image-based PDFs
 * 3. Consistent spinner animation for all loading states
 * 4. SIMPLIFIED: Removed uncertain metadata (page count, author, word count)
 * 5. ADDED: HTML preview support with zooming functionality
 * 6. FIXED: Double scrollbar issue in HTML preview
 * 
 * @param {Object} props
 * @param {Object} props.file - The file object to preview
 * @param {Function} props.onClose - Function to call when preview is closed
 */
const FilePreview = ({ file, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  
  // Handle file loading and processing
  useEffect(() => {
    const processFile = async () => {
      if (!file) {
        setError("No file provided");
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Determine file type for preview
        let filePreviewData;
        
        if (file.isImage || file.type?.startsWith('image/')) {
          // Image previews
          filePreviewData = {
            type: 'image',
            dataUrl: file.dataUrl,
            width: file.width,
            height: file.height
          };
        }
        else if (file.isPDF || file.type === 'application/pdf' || file.name?.endsWith('.pdf')) {
          // PDF preview
          if (!file.dataUrl && !file.buffer) {
            throw new Error("PDF file doesn't contain viewable data");
          }
          
          filePreviewData = {
            type: 'pdf',
            dataUrl: file.dataUrl,
            buffer: file.buffer,
            name: file.name,
            size: file.size
          };
          
          // Try to generate a thumbnail and extract text with enhanced handling for image PDFs
          try {
            const source = file.dataUrl || file.buffer;
            if (source) {
              // Extract text in background
              try {
                const extracted = await extractTextFromPDF(source);
                filePreviewData.text = extracted.text || '';
                filePreviewData.metadata = {
                  pageCount: extracted.pageCount || '?',
                  title: extracted.metadata?.title || file.name,
                  author: extracted.metadata?.author || 'Unknown',
                  creationDate: extracted.metadata?.creationDate
                };
              } catch (textErr) {
                console.warn("Text extraction failed, possibly an image-based PDF:", textErr);
                // Continue anyway, this could be an image-based PDF
                filePreviewData.isImageBasedPdf = true;
              }
              
              // Generate thumbnail with enhanced settings for image-based PDFs
              try {
                console.log("Generating high-quality PDF thumbnail with enhanced settings...");
                const thumbnail = await generatePDFThumbnail(source, { 
                  page: 1, 
                  width: 800,
                  height: 1200,
                  useHighQualityRendering: true, // Always use high quality for best results
                  scale: 2.0 // Higher scale for better image rendering
                });
                filePreviewData.thumbnail = thumbnail;
                
                // If we have a thumbnail but no text, it's likely an image-based PDF
                if (thumbnail && (!filePreviewData.text || filePreviewData.text.trim() === '')) {
                  filePreviewData.isImageBasedPdf = true;
                }
              } catch (thumbnailErr) {
                console.warn("Standard thumbnail generation failed, trying image-based fallback:", thumbnailErr);
                
                // Try image-based fallback for PDFs containing only images
                try {
                  const imageThumbnail = createImageBasedPdfThumbnail(
                    "Image-based PDF"
                  );
                  filePreviewData.thumbnail = imageThumbnail;
                  filePreviewData.isImageBasedPdf = true;
                } catch (fallbackErr) {
                  console.error("All thumbnail generation methods failed:", fallbackErr);
                  // Create a basic error thumbnail
                  filePreviewData.thumbnail = generatePdfErrorThumbnail("PDF Preview Failed");
                }
              }
            }
          } catch (pdfErr) {
            console.warn("PDF processing error:", pdfErr);
            // Still continue with what we have
          }
        }
        else if (file.isCSV || file.type === 'text/csv' || file.name?.endsWith('.csv')) {
          // CSV preview
          if (!file.content) {
            throw new Error("CSV content is missing");
          }
          
          filePreviewData = {
            type: 'csv',
            content: file.content,
            name: file.name,
            size: file.size
          };
          
          // Parse CSV content if not already parsed
          if (!file.parsed) {
            try {
              const parsedData = parseCSV(file.content);
              filePreviewData.parsed = parsedData;
              filePreviewData.tableHtml = formatCSVAsTable(parsedData, 100);
            } catch (parseErr) {
              console.warn("CSV parse error:", parseErr);
              // Still set the preview data without parsed content
            }
          } else {
            filePreviewData.parsed = file.parsed;
            filePreviewData.tableHtml = formatCSVAsTable(file.parsed, 100);
          }
        }
        else if (file.isExcel || 
                 file.type?.includes('spreadsheet') || 
                 file.type?.includes('excel') || 
                 file.name?.match(/\.(xlsx|xls|ods)$/i)) {
          // Excel preview
          if (!file.buffer) {
            throw new Error("Excel file buffer is missing");
          }
          
          filePreviewData = {
            type: 'excel',
            buffer: file.buffer,
            name: file.name,
            size: file.size
          };
          
          // Parse Excel buffer if not already parsed
          if (!file.parsed) {
            try {
              const parsedExcel = await parseExcel(file.buffer);
              filePreviewData.parsed = parsedExcel;
              filePreviewData.tableHtml = formatExcelAsHtml(parsedExcel, 50);
              
              // Set active sheet and sheet options
              if (parsedExcel.summary && parsedExcel.summary.sheetNames) {
                filePreviewData.sheetOptions = parsedExcel.summary.sheetNames;
                filePreviewData.activeSheet = parsedExcel.summary.sheetNames[0];
              }
            } catch (excelErr) {
              console.warn("Excel parse error:", excelErr);
              // Still set the preview data without parsed content
            }
          } else {
            filePreviewData.parsed = file.parsed;
            filePreviewData.tableHtml = formatExcelAsHtml(file.parsed, 50);
            
            // Set active sheet and sheet options
            if (file.parsed.summary && file.parsed.summary.sheetNames) {
              filePreviewData.sheetOptions = file.parsed.summary.sheetNames;
              filePreviewData.activeSheet = file.parsed.summary.sheetNames[0];
            }
          }
        }
        else if (file.isDocx || 
                 file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                 file.name?.endsWith('.docx')) {
          // DOCX preview
          filePreviewData = {
            type: 'docx',
            buffer: file.buffer,
            text: file.text,
            name: file.name,
            size: file.size
          };
          
          // Try to convert DOCX to HTML if mammoth.js is available
          try {
            // Only attempt if we don't already have text content
            if (file.buffer && (!file.text || !file.html)) {
              const mammoth = await import('mammoth');
              if (mammoth) {
                const result = await mammoth.default.convertToHtml({ 
                  arrayBuffer: file.buffer 
                });
                
                filePreviewData.html = result.value;
                
                // If we don't already have text, extract it from the HTML
                if (!file.text) {
                  const text = result.value.replace(/<[^>]*>/g, ' ')
                                        .replace(/\s+/g, ' ')
                                        .trim();
                  filePreviewData.text = text;
                }
              }
            }
          } catch (mammothErr) {
            console.warn("Mammoth.js not available or conversion failed:", mammothErr);
            // Continue with just text display if HTML conversion fails
          }
          
          // Calculate word count if we have text
          if (filePreviewData.text) {
            const words = filePreviewData.text.trim().split(/\s+/);
            filePreviewData.wordCount = words.length;
          }
        }
        else if (file.isHtml || file.type === 'text/html' || 
                 file.name?.endsWith('.html') || file.name?.endsWith('.htm')) {
          // HTML preview - new handler
          if (!file.content) {
            throw new Error("HTML content is missing");
          }
          
          filePreviewData = {
            type: 'html',
            content: file.content,
            name: file.name,
            size: file.size
          };
        }
        else if (file.isText || file.type?.startsWith('text/') || 
                 file.name?.match(/\.(txt|js|py|java|cpp|css|html|json|md)$/i)) {
          // Text file preview
          if (!file.content) {
            throw new Error("Text content is missing");
          }
          
          filePreviewData = {
            type: 'text',
            content: file.content,
            name: file.name,
            size: file.size,
            language: getLanguageFromFileName(file.name)
          };
        }
        else {
          // Generic file type - just show metadata
          filePreviewData = {
            type: 'unsupported',
            name: file.name,
            type: file.type,
            size: file.size
          };
        }
        
        setPreviewData(filePreviewData);
      } catch (err) {
        console.error("Error preparing file preview:", err);
        setError(err.message || "Failed to prepare file preview");
      } finally {
        setLoading(false);
      }
    };
    
    processFile();
  }, [file]);
  
  // Helper function to get language from file name for syntax highlighting
  const getLanguageFromFileName = (filename) => {
    if (!filename) return null;
    
    const extension = filename.split('.').pop().toLowerCase();
    
    const extensionMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'rb': 'ruby',
      'php': 'php',
      'java': 'java',
      'cs': 'csharp',
      'cpp': 'cpp',
      'c': 'c',
      'h': 'c',
      'go': 'go',
      'rs': 'rust',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'sass': 'sass',
      'json': 'json',
      'md': 'markdown',
      'xml': 'xml',
      'yml': 'yaml',
      'yaml': 'yaml',
      'sh': 'bash',
      'bash': 'bash',
      'sql': 'sql'
    };
    
    return extensionMap[extension] || null;
  };
  
  // Handle escape key to close preview
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  
  // Safe close handler
  const handleClose = (e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    onClose();
  };
  
  // Render the preview based on file type and state
  const renderPreview = () => {
    if (loading) {
      return (
        <div className="file-preview-loading">
          <Spinner 
            size="medium" 
            label="Loading preview..." 
          />
        </div>
      );
    }
    
    if (error) {
      return <ErrorPreview message={error} />;
    }
    
    if (!previewData) {
      return <ErrorPreview message="No preview available" />;
    }
    
    // Render different preview components based on file type
    switch (previewData.type) {
      case 'image':
        return <ImagePreview data={previewData} />;
        
      case 'pdf':
        return <SimplePDFPreview data={previewData} />;
        
      case 'csv':
        return <SpreadsheetPreview data={previewData} type="csv" />;
        
      case 'excel':
        return <SpreadsheetPreview data={previewData} type="excel" />;
        
      case 'docx':
        return <DocxPreview data={previewData} />;
        
      case 'html':
        return <HtmlPreview data={previewData} />;
        
      case 'text':
        return <TextPreview data={previewData} />;
        
      case 'unsupported':
      default:
        return (
          <div className="unsupported-preview">
            <div className="unsupported-icon">📄</div>
            <p>Preview not available for this file type.</p>
            <p>File: {previewData.name}</p>
            <p>Type: {previewData.type || 'Unknown'}</p>
            <p>Size: {formatFileSize(previewData.size)}</p>
          </div>
        );
    }
  };
  
  return (
    <div className="file-preview-overlay" onClick={handleClose}>
      <div 
        className="file-preview-container" 
        onClick={(e) => {
          if (typeof e.stopPropagation === 'function') {
            e.stopPropagation();
          }
        }}
      >
        <div className="file-preview-header">
          <h3>{file?.name || 'File Preview'}</h3>
          <button className="close-preview" onClick={handleClose} aria-label="Close preview">×</button>
        </div>
        <div className="file-preview-content">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

/* Internal components for specific file types below */

// HTML Preview Component with improved zooming functionality and fixed scrollbar issues
const HtmlPreview = ({ data }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showSource, setShowSource] = useState(false);
  const [scale, setScale] = useState(0.5); // Start with a lower default scale to avoid initial oversizing
  const [contentDimensions, setContentDimensions] = useState({ width: 800, height: 600 }); // Default dimensions
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [zoomLevel, setZoomLevel] = useState(50); // Match initial scale
  const [isStabilizing, setIsStabilizing] = useState(true); // Track initial stabilization period
  const [hasFit, setHasFit] = useState(false); // Track if fit has been applied
  
  // Refs
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const dimensionsDetectedRef = useRef(false);
  
  // Handle loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  // Set initial scale and schedule a fit calculation after render
  useEffect(() => {
    if (!showSource) {
      // Initial fit immediately when rendered
      const initialTimer = setTimeout(() => {
        // First measure container
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setContainerSize({
            width: rect.width,
            height: rect.height
          });
        }
        
        // Force handleZoomReset to run after component is fully rendered
        handleZoomReset();
        
        // Track that fit has been applied
        setHasFit(true);
        
        // After initial fit, wait a bit and mark as stabilized
        setTimeout(() => {
          setIsStabilizing(false);
          // Second fit when stabilized
          handleZoomReset();
        }, 600);
      }, 100); // Run sooner
      
      // Second fit after all content should be loaded
      const secondFitTimer = setTimeout(() => {
        handleZoomReset();
      }, 1000);
      
      return () => {
        clearTimeout(initialTimer);
        clearTimeout(secondFitTimer);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, showSource]);
  
  // Toggle between rendered view and source code
  const toggleView = () => {
    setShowSource(!showSource);
    // Reset dimensions detection flag when toggling
    dimensionsDetectedRef.current = false;
    // Reset stabilizing state when toggling to rendered view
    if (showSource) {
      setIsStabilizing(true);
      setTimeout(() => {
        setIsStabilizing(false);
      }, 800);
    }
  };
  
  // Handle container size measurement
  useEffect(() => {
    if (containerRef.current && !showSource) {
      const measureContainer = () => {
        const rect = containerRef.current.getBoundingClientRect();
        const newSize = {
          width: rect.width,
          height: rect.height
        };
        setContainerSize(newSize);
      };
      
      // Set minimum dimensions on container for better display
      if (containerRef.current) {
        containerRef.current.style.minHeight = '600px';
      }
      
      // Initial measurement
      measureContainer();
      
      // Re-measure on window resize
      window.addEventListener('resize', measureContainer);
      return () => {
        window.removeEventListener('resize', measureContainer);
      };
    }
  }, [showSource]);
  
  // Direct dimensions detection from iframe (alternative method)
  const detectIframeDimensions = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        // Wait a moment to ensure content is loaded
        setTimeout(() => {
          const width = Math.max(
            iframeRef.current.contentWindow.document.body.scrollWidth,
            iframeRef.current.contentWindow.document.documentElement.scrollWidth,
            800 // Minimum width
          );
          
          const height = Math.max(
            iframeRef.current.contentWindow.document.body.scrollHeight,
            iframeRef.current.contentWindow.document.documentElement.scrollHeight,
            600 // Minimum height
          );
          
          console.log("Directly detected dimensions:", width, height);
          
          // Only update if dimensions are actually detected and significant change
          if (width > 0 && height > 0 && 
              (Math.abs(width - contentDimensions.width) > 20 || 
               Math.abs(height - contentDimensions.height) > 20)) {
            setContentDimensions({ width, height });
            dimensionsDetectedRef.current = true;
            
            // Recalculate the appropriate scale
            setTimeout(handleZoomReset, 100);
          }
        }, 200);
      } catch (err) {
        console.warn("Error accessing iframe content dimensions:", err);
      }
    }
  };
  
  // Handle zoom reset - improved version that works reliably
  const handleZoomReset = () => {
    // Debug log current state
    console.log("Calculating fit. Container:", containerSize, "Content:", contentDimensions);
    
    // Use safe defaults if dimensions are missing or zero
    const contentWidth = contentDimensions.width || 800;
    const contentHeight = contentDimensions.height || 600;
    
    // Make sure we have container dimensions
    if (containerSize.width === 0 || containerSize.height === 0) {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({
          width: rect.width,
          height: rect.height
        });
      }
    }
    
    const viewportWidth = containerSize.width || containerRef.current?.clientWidth || 800;
    const viewportHeight = containerSize.height || containerRef.current?.clientHeight || 600;
    
    // Calculate padding
    const containerPadding = 40;
    const containerWidth = viewportWidth - containerPadding;
    const containerHeight = viewportHeight - containerPadding;
    
    // Calculate scale factors for width and height
    const widthScale = containerWidth / contentWidth;
    const heightScale = containerHeight / contentHeight;
    
    // Use minimum scale of 0.4 (40%) to prevent tiny content
    // Use a more conservative scale - prefer slightly smaller than bigger
    const fitScale = Math.max(Math.min(widthScale, heightScale) * 0.95, 0.4);
    
    console.log("Calculated fit scale:", fitScale);
    
    // Apply scale with visual feedback
    setScale(fitScale);
    setZoomLevel(Math.round(fitScale * 100));
    
    // Try to directly measure dimensions if we haven't detected them yet
    if (!dimensionsDetectedRef.current) {
      detectIframeDimensions();
    }
  };
  
  // Zoom controls
  const handleZoomIn = () => {
    setScale(prev => {
      const newScale = Math.min(prev + 0.1, 1.5);
      setZoomLevel(Math.round(newScale * 100));
      return newScale;
    });
  };
  
  const handleZoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev - 0.1, 0.1);
      setZoomLevel(Math.round(newScale * 100));
      return newScale;
    });
  };
  
  // Create a safe display of HTML with zoom controls - improved version with fixed double scrollbar
  const sanitizeAndDisplayHtml = () => {
    try {
      // Create a sandboxed and restricted environment for the HTML
      // with a wrapper for scaling and better dimensions
      return (
        <div className="html-iframe-wrapper" 
          style={{ 
            transform: `scale(${scale})`, 
            transformOrigin: 'center top', // Center horizontally, align to top
            width: `${contentDimensions.width}px`,
            height: `${contentDimensions.height}px`,
            margin: '0 auto', // Center in container
            opacity: isStabilizing ? 0.1 : 1, // Fade in after stabilizing
            visibility: isStabilizing ? 'hidden' : 'visible', // Hide during stabilization
            overflow: 'hidden', // Prevent scrollbars on the wrapper
            transition: 'transform 0.3s ease-out, opacity 0.3s ease-out' // Smooth transitions
          }}
        >
          <iframe 
            ref={iframeRef}
            title="HTML Preview"
            srcDoc={`
              <!DOCTYPE html>
              <html>
                <head>
                  <meta charset="UTF-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <style>
                    body { 
                      margin: 0; 
                      padding: 0;
                      min-width: 600px;
                      min-height: 400px;
                      overflow: visible !important; /* Force content to be visible without scrollbars */
                      -ms-overflow-style: none; /* Hide scrollbars in IE/Edge */
                      scrollbar-width: none; /* Hide scrollbars in Firefox */
                    }
                    body::-webkit-scrollbar {
                      display: none; /* Hide scrollbars in WebKit browsers */
                    }
                    html {
                      overflow: visible !important; /* Force content visibility */
                      -ms-overflow-style: none;
                      scrollbar-width: none;
                    }
                    html::-webkit-scrollbar {
                      display: none;
                    }
                  </style>
                  <script>
                    // Send dimensions to parent when loaded
                    window.onload = function() {
                      const width = Math.max(
                        document.body.scrollWidth,
                        document.documentElement.scrollWidth,
                        800 // Minimum width
                      );
                      const height = Math.max(
                        document.body.scrollHeight,
                        document.documentElement.scrollHeight,
                        600 // Minimum height
                      );
                      // Send dimensions to parent
                      window.parent.postMessage({ type: 'dimensions', width, height }, '*');
                      
                      // Schedule multiple measurements to ensure accurate dimensions
                      const measureTimes = [100, 300, 500, 1000];
                      measureTimes.forEach(timeout => {
                        setTimeout(function() {
                          const updatedWidth = Math.max(
                            document.body.scrollWidth,
                            document.documentElement.scrollWidth,
                            800
                          );
                          const updatedHeight = Math.max(
                            document.body.scrollHeight,
                            document.documentElement.scrollHeight,
                            600
                          );
                          window.parent.postMessage({ 
                            type: 'dimensions', 
                            width: updatedWidth, 
                            height: updatedHeight 
                          }, '*');
                        }, timeout);
                      });
                    };
                  </script>
                </head>
                <body>${data.content}</body>
              </html>
            `}
            sandbox="allow-scripts allow-same-origin"
            className="html-iframe"
            style={{ 
              overflow: 'visible', // Show full content
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: 'white'
            }} 
            onLoad={() => {
              setIsLoading(false);
              // Try to get dimensions directly on load
              detectIframeDimensions();
              // Force a fit calculation after content has loaded
              setTimeout(handleZoomReset, 100);
            }}
          />
        </div>
      );
    } catch (error) {
      console.error("Error displaying HTML:", error);
      return (
        <div className="html-error">
          <p>Error displaying HTML content. The file may contain invalid HTML.</p>
        </div>
      );
    }
  };
  
  // Listen for iframe messages to get content dimensions - with stability improvements
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'dimensions') {
        console.log("Received dimensions from iframe:", event.data);
        
        // Only update if dimensions are actually valid and significantly different
        if (event.data.width > 0 && event.data.height > 0) {
          // Check if the dimensions are substantially different to avoid constant updates
          const significantChange = 
            !dimensionsDetectedRef.current || // First detection
            Math.abs(event.data.width - contentDimensions.width) > 50 || // Or big width change
            Math.abs(event.data.height - contentDimensions.height) > 50; // Or big height change
          
          if (significantChange) {
            setContentDimensions({
              width: event.data.width,
              height: event.data.height
            });
            dimensionsDetectedRef.current = true;
            
            // Schedule a fit calculation after dimensions update
            setTimeout(handleZoomReset, 100);
          }
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // More conservative retry approach with fewer attempts
    // Only do one retry after 500ms - others happen on demand in critical spots
    const retryTimer = setTimeout(() => {
      if (!dimensionsDetectedRef.current && !showSource) {
        console.log("Retry dimension detection after initial delay");
        detectIframeDimensions();
      }
    }, 500);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(retryTimer);
    };
  }, [showSource]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return (
    <div className="html-preview">
      <div className="html-info">
        <h3>{data.name}</h3>
        <div className="html-metadata">
          <p><strong>Type:</strong> HTML Document</p>
          <p><strong>Size:</strong> {formatFileSize(data.size)}</p>
        </div>
      </div>
      
      <div className="html-controls">
        <button 
          className="html-toggle-button"
          onClick={toggleView}
        >
          {showSource ? "View Rendered HTML" : "View Source Code"}
        </button>
        
        {!showSource && (
          <div className="html-zoom-controls">
            <button className="zoom-button" onClick={handleZoomOut}>
              🔍-
            </button>
            <span className="zoom-level">{zoomLevel}%</span>
            <button className="zoom-button" onClick={handleZoomIn}>
              🔍+
            </button>
            <button className="zoom-reset" onClick={handleZoomReset}>
              Fit
            </button>
          </div>
        )}
        
        <div className="html-security-notice">
          <span className="security-icon">🔒</span>
          <span>HTML is displayed in a secure sandbox</span>
        </div>
      </div>
      
      <div 
        className="html-content-container" 
        ref={containerRef}
      >
        {isLoading ? (
          <div className="html-loading">
            <Spinner 
              size="medium" 
              label="Loading HTML content..." 
            />
          </div>
        ) : showSource ? (
          <pre className="html-source-code">
            <code>
              {data.content}
            </code>
          </pre>
        ) : (
          <div className="html-render-container">
            {sanitizeAndDisplayHtml()}
            
            {/* Show a loading spinner during stabilization phase */}
            {isStabilizing && !isLoading && (
              <div className="html-stabilizing">
                <Spinner 
                  size="small" 
                  label="Optimizing view..." 
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// PDF Preview Component with improved loading state
const SimplePDFPreview = ({ data }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Simulate loading completion or track actual loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600); // Shorter loading time for better UX
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="pdf-preview">
      <div className="pdf-info">
        <h3>{data.metadata?.title || data.name}</h3>
        <div className="pdf-metadata">
          {/* Page count field removed */}
          {/* Author field removed */}
          {data.metadata?.creationDate && (
            <p><strong>Created:</strong> {new Date(data.metadata.creationDate).toLocaleDateString()}</p>
          )}
          <p><strong>Size:</strong> {formatFileSize(data.size)}</p>
          {data.isImageBasedPdf && (
            <p><strong>Type:</strong> Image-based PDF</p>
          )}
        </div>
      </div>
      
      {/* PDF viewer with improved loading state */}
      <div className="pdf-viewer">
        {isLoading ? (
          <div className="pdf-loading-container">
            <Spinner 
              size="medium" 
              color="#0078ff"
              label="Loading PDF preview..." 
            />
          </div>
        ) : data.thumbnail ? (
          <div className="pdf-thumbnail">
            <img src={data.thumbnail} alt="PDF preview" />
            <div className="thumbnail-caption">
              PDF Preview - First Page
              {data.isImageBasedPdf && (
                <div className="image-pdf-notice">
                  This appears to be an image-based PDF
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="pdf-no-preview">
            <p>PDF preview unavailable.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Spreadsheet Preview Component with loading state
const SpreadsheetPreview = ({ data, type }) => {
  const [activeSheet, setActiveSheet] = useState(data.activeSheet || null);
  const [tableHtml, setTableHtml] = useState(data.tableHtml || '');
  const [isLoading, setIsLoading] = useState(true);
  
  // Simulate or track loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 400); // Short timeout for spreadsheets
    
    return () => clearTimeout(timer);
  }, []);
  
  // Handle sheet selection change for Excel files
  const handleSheetChange = (e) => {
    setIsLoading(true);
    setActiveSheet(e.target.value);
    
    if (data.parsed && data.parsed.sheets) {
      // Update the table HTML to show the selected sheet
      try {
        const singleSheetData = {
          sheets: { [e.target.value]: data.parsed.sheets[e.target.value] },
          summary: data.parsed.summary
        };
        
        setTableHtml(formatExcelAsHtml(singleSheetData, 50));
        
        // Short delay to show loading state
        setTimeout(() => setIsLoading(false), 300);
      } catch (err) {
        console.error("Error updating sheet view:", err);
        setIsLoading(false);
      }
    }
  };
  
  return (
    <div className="spreadsheet-preview">
      <div className="spreadsheet-info">
        <h3>{data.name}</h3>
        <div className="spreadsheet-metadata">
          <p><strong>Type:</strong> {type === 'csv' ? 'CSV Spreadsheet' : 'Excel Spreadsheet'}</p>
          {type === 'csv' && data.parsed && (
            <>
              <p><strong>Rows:</strong> {data.parsed.rows?.length || 0}</p>
              <p><strong>Columns:</strong> {data.parsed.headers?.length || 0}</p>
            </>
          )}
          {type === 'excel' && data.parsed && (
            <>
              <p><strong>Sheets:</strong> {data.parsed.summary?.sheetCount || 0}</p>
              <p><strong>Total Rows:</strong> {data.parsed.summary?.totalRows || 0}</p>
            </>
          )}
          <p><strong>Size:</strong> {formatFileSize(data.size)}</p>
        </div>
      </div>
      
      {/* Excel sheet selector */}
      {type === 'excel' && data.sheetOptions && data.sheetOptions.length > 0 && (
        <div className="sheet-selector">
          <label htmlFor="sheet-select">Sheet: </label>
          <select 
            id="sheet-select" 
            value={activeSheet || ''} 
            onChange={handleSheetChange}
            className="sheet-select"
          >
            {data.sheetOptions.map(sheet => (
              <option key={sheet} value={sheet}>{sheet}</option>
            ))}
          </select>
        </div>
      )}
      
      <div className="spreadsheet-table-container">
        {isLoading ? (
          <div className="spreadsheet-loading">
            <Spinner 
              size="medium" 
              label={`Loading ${type === 'csv' ? 'CSV' : 'spreadsheet'} data...`} 
            />
          </div>
        ) : tableHtml ? (
          <div 
            className="spreadsheet-table"
            dangerouslySetInnerHTML={{ __html: tableHtml }}
          />
        ) : data.content ? (
          <pre className="spreadsheet-raw-content">{data.content}</pre>
        ) : (
          <div className="spreadsheet-no-data">
            <p>No data available to display.</p>
          </div>
        )}
      </div>
      
      {/* Small indicators for limited data */}
      {!isLoading && (type === 'csv' && data.parsed && data.parsed.rows && data.parsed.rows.length > 100) && (
        <div className="data-limit-note">
          <p>Showing first 100 rows of {data.parsed.rows.length} total rows</p>
        </div>
      )}
      
      {!isLoading && (type === 'excel' && data.parsed && data.parsed.sheets && activeSheet && 
        data.parsed.sheets[activeSheet]?.rowCount > 50) && (
        <div className="data-limit-note">
          <p>Showing first 50 rows of {data.parsed.sheets[activeSheet].rowCount} rows in sheet '{activeSheet}'</p>
        </div>
      )}
    </div>
  );
};

// DOCX Preview Component with loading state
const DocxPreview = ({ data }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Simulate or track loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500); // Medium timeout for DOCX rendering
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div className="docx-preview">
      <div className="docx-info">
        <h3>{data.name}</h3>
        <div className="docx-metadata">
          <p><strong>Type:</strong> Word Document</p>
          {/* Word Count field removed */}
          <p><strong>Size:</strong> {formatFileSize(data.size)}</p>
        </div>
      </div>
      
      <div className="docx-content">
        {isLoading ? (
          <div className="docx-loading">
            <Spinner 
              size="medium"
              label="Loading document..." 
            />
          </div>
        ) : data.html ? (
          <div 
            className="docx-html-content"
            dangerouslySetInnerHTML={{ __html: data.html }}
          />
        ) : data.text ? (
          <div className="docx-text-content">
            {data.text.split('\n').map((line, i) => (
              <p key={i}>{line || ' '}</p>
            ))}
          </div>
        ) : (
          <div className="docx-no-content">
            <p>No document content available to display.</p>
          </div>
        )}
      </div>
      
      {/* Note if we're just showing text instead of formatted HTML */}
      {!isLoading && !data.html && data.text && (
        <div className="content-note">
          <p>Showing plain text content. For better formatting, view in a Word processor.</p>
        </div>
      )}
    </div>
  );
};

// Text Preview Component with loading state
const TextPreview = ({ data }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Simulate or track loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300); // Short timeout for text files
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div className="text-preview">
      <div className="text-info">
        <h3>{data.name}</h3>
        <p><strong>Size:</strong> {formatFileSize(data.size)}</p>
        {data.language && <p><strong>Language:</strong> {data.language}</p>}
      </div>
      
      <div className="text-content-container">
        {isLoading ? (
          <div className="text-loading">
            <Spinner 
              size="medium" 
              label="Loading text content..." 
            />
          </div>
        ) : data.language ? (
          // With syntax highlighting if we have a recognized language
          <pre className={`language-${data.language}`}>
            <code>{data.content}</code>
          </pre>
        ) : (
          // Plain text for unknown file types
          <pre className="text-content">
            {data.content}
          </pre>
        )}
      </div>
      
      {/* Line count */}
      {!isLoading && (
        <div className="text-stats">
          <p>
            {data.content?.split('\n').length || 0} lines, 
            {' '}{data.content?.length || 0} characters
          </p>
        </div>
      )}
    </div>
  );
};

// Image Preview Component with loading state
const ImagePreview = ({ data }) => {
  const [zoom, setZoom] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 300));
  };
  
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };
  
  const handleZoomReset = () => {
    setZoom(100);
  };
  
  // Handle image loading
  const handleImageLoad = () => {
    setIsLoading(false);
  };
  
  return (
    <div className="image-preview">
      <div className="image-info">
        <h3>{data.name}</h3>
        <div className="image-metadata">
          {data.width && data.height && (
            <p><strong>Dimensions:</strong> {data.width} × {data.height}px</p>
          )}
          {data.size && <p><strong>Size:</strong> {formatFileSize(data.size)}</p>}
        </div>
      </div>
      
      <div className="image-controls">
        <button className="zoom-button" onClick={handleZoomOut}>
          🔍-
        </button>
        <span className="zoom-level">{zoom}%</span>
        <button className="zoom-button" onClick={handleZoomIn}>
          🔍+
        </button>
        <button className="zoom-reset" onClick={handleZoomReset}>
          Reset
        </button>
      </div>
      
      <div className="image-container">
        {isLoading && (
          <div className="image-loading">
            <Spinner 
              size="medium"
              label="Loading image..." 
            />
          </div>
        )}
        
        {data.dataUrl ? (
          <img 
            src={data.dataUrl} 
            alt={data.name || "Image preview"} 
            style={{ 
              transform: `scale(${zoom/100})`,
              transformOrigin: 'top left',
              maxWidth: zoom > 100 ? 'none' : '100%',
              height: 'auto',
              display: isLoading ? 'none' : 'block'
            }}
            onLoad={handleImageLoad}
          />
        ) : (
          <div className="image-error">
            <p>Image data is not available for preview.</p>
          </div>
        )}
      </div>
      
      {/* Image action buttons removed */}
    </div>
  );
};

// Error Preview Component
const ErrorPreview = ({ message }) => {
  return (
    <div className="error-preview">
      <div className="error-icon">⚠️</div>
      <h3>Preview Error</h3>
      <p>{message || "An error occurred while attempting to preview this file."}</p>
      <div className="error-suggestions">
        <p>Suggestions:</p>
        <ul>
          <li>Make sure the file is not corrupted</li>
          <li>Try downloading the file and viewing it in its native application</li>
          <li>Try uploading the file again</li>
          <li>Some files may not be previewable in the browser</li>
        </ul>
      </div>
    </div>
  );
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default FilePreview;